import { useMidi } from '@tunasong/audio-ui'
import { addToolBreadcrumb } from '@tunasong/models'
import {
  MelodyLib,
  VocalLib,
  computeMelodyTrainerStats,
  referenceSoundsDuringTake,
  toPhraseFeedback,
  toTimingFeedback,
  type CaptureDiagnosis,
  type MelodyTrainerAttempt,
  type MelodyTrainerMode,
  type PhraseFeedback,
  type PracticeLevel,
  type TimingFeedback,
} from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { useUserConfig } from '@tunasong/ui-lib'
import { useEffect, useRef, useState } from 'react'
import type { FC } from 'react'
import { AUDIO_LEAD_IN_MS } from '../melody-capture/capture-take.js'
import { buildClickTrack, CLICK_INSTRUMENT } from '../melody-capture/click-track.js'
import { hasHeadphoneConsent, withHeadphoneConsent, withoutHeadphoneConsent } from '../melody-capture/headphone-consent.js'
import { useAudioCapture } from '../melody-capture/use-audio-capture.js'
import { useMidiCapture } from '../melody-capture/use-midi-capture.js'
import { useMelodyRollPlayback } from '../melody-roll/use-melody-roll-playback.js'
import { PitchCalibrationPanel } from '../pitch-training/pitch-calibration-panel.js'
import { initialCalibration, type CalibrationState } from '../pitch-training/pitch-calibration.js'
import { runCalibrationTake } from '../pitch-training/run-calibration-take.js'
import { useCaptureMonitor } from '../pitch-training/use-capture-monitor.js'
import { usePitchCapture } from '../pitch-training/use-pitch-capture.js'
import { usePlayNotes } from '../player/piano-player.hook.js'
import type { Stab } from '../player/stab-scheduler.js'
import { shouldWithholdScore } from './bleed-gate.js'
import { MelodyTrainerBleed } from './melody-trainer-bleed.js'
import { MelodyTrainerFeedback } from './melody-trainer-feedback.js'
import { MelodyTrainerQuestion } from './melody-trainer-question.js'
import { MelodyTrainerSummary } from './melody-trainer-summary.js'
import { MelodyTrainerWelcome } from './melody-trainer-welcome.js'
import { runTrainerAttempt, type AttemptPhase } from './trainer-attempt.js'
import { attemptTimeline } from './trainer-timeline.js'
import styles from './melody-trainer.module.css'

/**
 * Melody Trainer v1 - sing or play a melody back, scored on pitch AND timing.
 *
 * Every attempt is started by a click, never by an effect. Two reasons, and the second is the
 * load-bearing one: a browser will not resume an `AudioContext` without a user gesture, and an
 * effect re-firing on an unstable dependency would re-open the microphone mid-take. The same rule
 * the Pitch Trainer and Melody Capture both state, for the same reason.
 *
 * All three modes score through ONE path: `alignNotes` decides which note is which, `scorePhrase`
 * judges the pitches, `scoreTiming` measures the onsets on the pairs the aligner chose. Three
 * paths would be three sets of rules about what a wrong note is, and they would drift. What
 * differs per mode is only what a performance is made of before it reaches that path - segmented
 * frames, or MIDI notes.
 *
 * `alignNotes` is called here rather than plumbed out of `scorePhrase`: it is pure and
 * deterministic, so the two calls agree by construction, and `VocalLib` is not this build's to
 * change.
 *
 * There is no input-latency calibration and no per-device latency stored anywhere. Nothing is
 * subtracted from a performed onset: `scoreTiming` measures every note from the phrase's own mean,
 * which absorbs whatever constant delay the path added. See `score-timing.ts` for why the tap test
 * that used to gate this screen was measuring the wrong path (Bowie, 2026-09-13).
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6
 */

/**
 * §6: sessions of N attempts on the same melody, default 5.
 *
 * Module-private, as the Pitch Trainer's `ITEMS_PER_SESSION` is: a session length is this file's
 * default, not an API, and exporting it from a component module breaks fast refresh.
 */
const ATTEMPTS_PER_SESSION = 5

const wait = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms))

type TrainerState =
  | { phase: 'welcome'; preSelected: MelodyTrainerMode | null; level: PracticeLevel }
  | {
      /**
       * The vocal range measurement, run here rather than sending the singer to another tool. It
       * does not gate `Start` - §5.7's transposition is an accommodation for a melody that already
       * exists at a pitch, not a prerequisite (Bowie, 2026-09-11) - but with no way to reach it
       * from here, a singer who had never opened the Pitch Trainer could never be accommodated.
       *
       * It carries the level so measuring a range does not silently drop the singer back to the
       * default exercise (Bowie, 2026-09-13).
       */
      phase: 'calibrating-range'
      level: PracticeLevel
      calibration: CalibrationState
      listening: boolean
      diagnosis: CaptureDiagnosis | null
    }
  | {
      phase: 'asking'
      mode: MelodyTrainerMode
      level: PracticeLevel
      index: number
      attempts: MelodyTrainerAttempt[]
      capture: AttemptPhase | null
    }
  | {
      phase: 'feedback'
      mode: MelodyTrainerMode
      level: PracticeLevel
      index: number
      attempts: MelodyTrainerAttempt[]
      pitch: PhraseFeedback
      timing: TimingFeedback | null
      diagnosis: CaptureDiagnosis | null
      attempt: MelodyTrainerAttempt
    }
  | {
      /**
       * §12.2: the take measured as playback rather than as a voice, so there is no score to show
       * and none was computed. Its own phase rather than a flag on `feedback`, because the two
       * states have nothing on screen in common - a percentage beside "that may have been the
       * melody" would be the melody scoring itself.
       */
      phase: 'bleed'
      mode: MelodyTrainerMode
      level: PracticeLevel
      index: number
      attempts: MelodyTrainerAttempt[]
    }
  | { phase: 'done'; mode: MelodyTrainerMode; level: PracticeLevel; attempts: MelodyTrainerAttempt[] }

const isAudioMode = (mode: MelodyTrainerMode) => mode !== 'midi'

/** Voice mode hears what it is asked to sing: the reference sounds at the transposed pitch too. */
const transposeMelody = (melody: Melody, semitones: number): Melody =>
  semitones === 0 ? melody : { ...melody, notes: melody.notes.map(note => ({ ...note, midi: note.midi + semitones })) }

/**
 * One attempt's contribution to the session summary.
 *
 * Pitch only. A session's timing lean used to be folded here too, and it cannot be: `scoreTiming`
 * measures every note from its own phrase's mean, so a per-attempt mean onset error is identically
 * zero and averaging those across a session would report "even" to everyone.
 */
const toAttempt = (pitch: PhraseFeedback): MelodyTrainerAttempt => ({
  meanScore: pitch.scorePercent === null ? null : pitch.scorePercent / 100,
})

export interface MelodyTrainerProps {
  melody: Melody
  /** Attempts in a session. Defaults to §6's five. */
  attempts?: number
  /** Present when the trainer is a dialog over a melody rather than its own page. */
  onClose?: () => void
}

export const MelodyTrainer: FC<MelodyTrainerProps> = props => {
  const { melody, attempts: attemptsPerSession = ATTEMPTS_PER_SESSION, onClose } = props

  const [userConfig, updateConfig] = useUserConfig()
  const audio = useAudioCapture()
  const midiCapture = useMidiCapture()
  const monitor = useCaptureMonitor()
  /**
   * A second capture, used only by the range measurement. The Pitch Trainer's, because the range
   * take is the Pitch Trainer's: it mutes playback for the window, which a take used to MEASURE a
   * voice must do and a melody take - whose click has to keep sounding - must not.
   */
  const rangeCapture = usePitchCapture()
  const { playNotes: playClickNotes } = usePlayNotes(CLICK_INSTRUMENT)

  const [state, setState] = useState<TrainerState>({ phase: 'welcome', preSelected: null, level: 'sing-along' })

  /** Every scheduled click, so Cancel and unmount can drop the ones that have not sounded. */
  const timeouts = useRef<number[]>([])

  /** Only to know whether a keyboard is connected; `useMidiCapture` is what records a take. */
  const { inputs: midiInputs } = useMidi()

  const clearTimeouts = () => {
    for (const handle of timeouts.current) {
      window.clearTimeout(handle)
    }
    timeouts.current = []
  }

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'melody-trainer' })
    return clearTimeouts
  }, [])

  const range = userConfig?.musicPreferences?.vocalRange ?? null
  const confirmedDevices = userConfig?.musicPreferences?.headphoneConfirmedDevices

  const referenceMidis = melody.notes.map(note => note.midi)
  const voiceMidis = range ? VocalLib.transposeIntoRange(referenceMidis, range) : referenceMidis
  const transposedBy = referenceMidis.length === 0 ? 0 : voiceMidis[0] - referenceMidis[0]

  const activeMode = state.phase === 'welcome' || state.phase === 'calibrating-range' ? null : state.mode
  const practiceMelody = activeMode === 'voice' ? transposeMelody(melody, transposedBy) : melody
  const playback = useMelodyRollPlayback({ melody: practiceMelody })

  const scheduleClicks = (stabs: Stab[]) => {
    for (const stab of stabs) {
      timeouts.current.push(window.setTimeout(() => playClickNotes(stab.events), stab.delayMs))
    }
  }

  // ───────────────────────── range calibration ─────────────────────────

  const runRangeTake = async (level: PracticeLevel, calibration: CalibrationState) => {
    monitor.reset()
    setState({ phase: 'calibrating-range', level, calibration, listening: true, diagnosis: null })

    const outcome = await runCalibrationTake(
      { setMuted: rangeCapture.setMuted, startFrames: rangeCapture.startFrames, wait, onFrame: monitor.onFrame },
      calibration
    )
    setState({
      phase: 'calibrating-range',
      level,
      calibration: outcome.next,
      listening: false,
      diagnosis: outcome.diagnosis,
    })

    if (outcome.range) {
      updateConfig('musicPreferences', { ...userConfig?.musicPreferences, vocalRange: outcome.range })
      addToolBreadcrumb('calibrated', { tool: 'melody-trainer' })
    }
  }

  // ───────────────────────── one attempt ─────────────────────────

  const runAttempt = async (
    mode: MelodyTrainerMode,
    level: PracticeLevel,
    index: number,
    previous: MelodyTrainerAttempt[]
  ) => {
    const timeline = attemptTimeline(practiceMelody)
    const clickTrack = buildClickTrack({
      bpm: practiceMelody.tempo.bpm,
      meter: practiceMelody.tempo.meter,
      bars: timeline.bars,
      /* The count-in already sounded, before the reference. This click starts ON beat 1. */
      countIn: false,
    })

    monitor.reset()
    if (mode === 'midi') {
      midiCapture.arm()
    }
    setState({ phase: 'asking', mode, level, index, attempts: previous, capture: null })

    const result = await runTrainerAttempt(
      {
        startFrames: isAudioMode(mode) ? audio.startFrames : undefined,
        /* `bars: 0` leaves exactly the two count-in knocks, at the melody's own tempo. */
        playCountIn: () =>
          scheduleClicks(
            buildClickTrack({ bpm: practiceMelody.tempo.bpm, meter: practiceMelody.tempo.meter, bars: 0 })
          ),
        playReference: playback.play,
        stopReference: playback.stop,
        playClick: () => scheduleClicks(clickTrack),
        wait,
        now: () => performance.now(),
        onPhase: capture =>
          setState(current =>
            current.phase === 'asking' && current.index === index ? { ...current, capture } : current
          ),
        onFrame: isAudioMode(mode) ? monitor.onFrame : undefined,
      },
      {
        ...timeline,
        leadInMs: isAudioMode(mode) ? AUDIO_LEAD_IN_MS : 0,
        referenceDuringTake: referenceSoundsDuringTake(level),
      }
    )
    clearTimeouts()

    const practiceMidis = practiceMelody.notes.map(note => note.midi)
    const raw =
      mode === 'midi'
        ? midiCapture.collect(result.t0WallMs, result.t0WallMs + timeline.takeMs + timeline.tailMs)
        : []
    const sung =
      mode === 'midi'
        ? MelodyLib.rawNotesToSungNotes(raw)
        : VocalLib.segmentNotes(VocalLib.voicedFrames(result.frames), { sampleRate: audio.sampleRate })
    const performedOnsetsMs =
      mode === 'midi' ? raw.map(note => note.onsetMs) : MelodyLib.segmentOnsetsMs(sung, result.t0Ms)

    /*
     * MIDI is scored STRICTLY: a keyboard has every octave, so an octave-out performance is wrong
     * notes rather than the range accommodation §5.7's transpose rule exists to give a voice or a
     * mic'd instrument whose range the melody may exceed (Bowie, 2026-09-09).
     */
    const pitchScore = VocalLib.scorePhrase(sung, practiceMidis, {
      octaveRule: mode === 'midi' ? 'strict' : 'transpose',
    })
    /*
     * One `alignNotes` call for the two consumers below. It is pure, so a second call would agree
     * with this one by construction - but the bleed check reads the sung notes THROUGH these pairs,
     * and a reader should not have to prove two calls match to know which notes it measured.
     */
    const pairs = VocalLib.alignNotes(sung, practiceMidis).pairs

    /*
     * §12.2, and only where the hazard exists. At Level 2 nothing is sounding during the take, so a
     * note-perfect take is a note-perfect take and must be scored as one; at Level 1 through a
     * microphone the same numbers may be the speakers. Gated on the CAUSE, the same predicate the
     * welcome screen makes the headphone confirmation mandatory with.
     */
    if (
      pitchScore &&
      shouldWithholdScore({
        level,
        mode,
        perNote: pitchScore.absolute.perNote,
        stability: pairs.map(pair => sung[pair.sungIndex].iqrCents),
      })
    ) {
      addToolBreadcrumb('performed', { tool: 'melody-trainer', mode, heard: false })
      setState({ phase: 'bleed', mode, level, index, attempts: previous })
      return
    }

    const events = MelodyLib.scheduleMelody(practiceMelody)
    const timingScore = MelodyLib.scoreTiming({
      pairs,
      performedOnsetsMs,
      referenceOnsetsMs: events.map(event => event.time),
      referenceOffsetsMs: practiceMelody.notes.map(note => note.offsetMs),
    })

    const pitch = toPhraseFeedback(pitchScore)
    /* Only when nothing was heard, and only where there was a microphone to diagnose. */
    const diagnosis = pitchScore === null && isAudioMode(mode) ? VocalLib.diagnoseCapture(result.frames) : null

    addToolBreadcrumb('performed', { tool: 'melody-trainer', mode, heard: !pitch.nothingHeard })

    setState({
      phase: 'feedback',
      mode,
      level,
      index,
      attempts: previous,
      pitch,
      timing: pitch.nothingHeard ? null : toTimingFeedback(timingScore),
      diagnosis,
      attempt: toAttempt(pitch),
    })
  }

  // ───────────────────────── render ─────────────────────────

  if (state.phase === 'welcome') {
    return (
      <div className={styles.container}>
        <MelodyTrainerWelcome
          melody={melody}
          preSelected={state.preSelected}
          range={range}
          deviceLabel={audio.deviceLabel}
          headphonesConfirmed={hasHeadphoneConsent(confirmedDevices, audio.deviceLabel)}
          midiAvailable={midiInputs.length > 0}
          transposedBy={transposedBy}
          level={state.level}
          onLevelChange={level => setState(current => (current.phase === 'welcome' ? { ...current, level } : current))}
          onConfirmHeadphones={confirmed =>
            updateConfig('musicPreferences', {
              ...userConfig?.musicPreferences,
              headphoneConfirmedDevices: confirmed
                ? withHeadphoneConsent(confirmedDevices, audio.deviceLabel)
                : withoutHeadphoneConsent(confirmedDevices, audio.deviceLabel),
            })
          }
          onCalibrateRange={() =>
            setState({
              phase: 'calibrating-range',
              level: state.level,
              calibration: initialCalibration,
              listening: false,
              diagnosis: null,
            })
          }
          onStart={mode => void runAttempt(mode, state.level, 0, [])}
        />
      </div>
    )
  }

  if (state.phase === 'calibrating-range') {
    return (
      <div className={styles.container}>
        <PitchCalibrationPanel
          state={state.calibration}
          listening={state.listening}
          diagnosis={state.diagnosis}
          meter={{
            frame: monitor.frame,
            noiseFloorRms: monitor.noiseFloorRms,
            deviceLabel: rangeCapture.deviceLabel,
          }}
          onSing={() => void runRangeTake(state.level, state.calibration)}
          /* Back to a voice session at the level they came from: the singer came here from voice. */
          onCancel={() => setState({ phase: 'welcome', preSelected: 'voice', level: state.level })}
          onDone={() => setState({ phase: 'welcome', preSelected: 'voice', level: state.level })}
        />
      </div>
    )
  }

  if (state.phase === 'asking') {
    return (
      <div className={styles.container}>
        <MelodyTrainerQuestion
          melody={practiceMelody}
          index={state.index}
          total={attemptsPerSession}
          level={state.level}
          phase={state.capture}
          playheadMs={playback.playheadMs}
          meter={
            isAudioMode(state.mode)
              ? { frame: monitor.frame, noiseFloorRms: monitor.noiseFloorRms, deviceLabel: audio.deviceLabel }
              : undefined
          }
        />
      </div>
    )
  }

  if (state.phase === 'feedback') {
    const attemptsSoFar = [...state.attempts, state.attempt]
    return (
      <div className={styles.container}>
        <MelodyTrainerQuestion
          melody={practiceMelody}
          index={state.index}
          total={attemptsPerSession}
          level={state.level}
          phase={null}
          playheadMs={playback.playheadMs}
        />
        <MelodyTrainerFeedback
          pitch={state.pitch}
          diagnosis={state.diagnosis}
          timing={state.timing}
          onRetry={() => void runAttempt(state.mode, state.level, state.index, state.attempts)}
          onNext={() => {
            if (state.index + 1 >= attemptsPerSession) {
              setState({ phase: 'done', mode: state.mode, level: state.level, attempts: attemptsSoFar })
              return
            }
            void runAttempt(state.mode, state.level, state.index + 1, attemptsSoFar)
          }}
        />
      </div>
    )
  }

  if (state.phase === 'bleed') {
    return (
      <div className={styles.container}>
        <MelodyTrainerBleed onRetake={() => void runAttempt(state.mode, state.level, state.index, state.attempts)} />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <MelodyTrainerSummary
        stats={computeMelodyTrainerStats(state.attempts)}
        level={state.level}
        onRestart={() => void runAttempt(state.mode, state.level, 0, [])}
        /* §12.1: the progression IS the exercise, so Level 2 on the same melody is one click. */
        onTryFromMemory={() => void runAttempt(state.mode, 'sing-back', 0, [])}
        onClose={onClose}
      />
    </div>
  )
}
